import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const SSO_TRANSACTION_KEY = 'vmp:sso-transaction';

const readSSOTransaction = () => {
    try {
        return JSON.parse(window.sessionStorage.getItem(SSO_TRANSACTION_KEY)) || null;
    } catch (_) {
        return null;
    }
};

const persistSSOTransaction = (info) => {
    if (!info) return;

    const { isProvideService, code_verifier, redirect_uri } = info;
    window.sessionStorage.setItem(SSO_TRANSACTION_KEY, JSON.stringify({
        isProvideService: Boolean(isProvideService),
        code_verifier: code_verifier || '',
        redirect_uri: redirect_uri || '',
    }));
};

const createInitialState = () => ({
    registration: {
        email: '',
        password: '',
        rePassword: '',
        captcha: '',
        timeExpire: null,
        policies: null,
    },
    sso: {
        info: readSSOTransaction(),
        accessToken: '',
        tempToken: '',
    },
});

let authFlowState = createInitialState();
const listeners = new Set();

const emitChange = () => listeners.forEach((listener) => listener(authFlowState));

export const updateRegistrationFlow = (registration) => {
    authFlowState = {
        ...authFlowState,
        registration: { ...authFlowState.registration, ...registration },
    };
    emitChange();
};

export const updateSSOFlow = (sso) => {
    authFlowState = {
        ...authFlowState,
        sso: { ...authFlowState.sso, ...sso },
    };
    if (Object.prototype.hasOwnProperty.call(sso, 'info')) persistSSOTransaction(sso.info);
    emitChange();
};

export const clearRegistrationFlow = () => {
    authFlowState = { ...authFlowState, registration: createInitialState().registration };
    emitChange();
};

export const clearSSOFlow = () => {
    window.sessionStorage.removeItem(SSO_TRANSACTION_KEY);
    authFlowState = { ...authFlowState, sso: createInitialState().sso };
    emitChange();
};

export const resetAuthFlow = () => {
    window.sessionStorage.removeItem(SSO_TRANSACTION_KEY);
    authFlowState = createInitialState();
    emitChange();
};

const useAuthFlowStore = () => {
    const [state, setState] = useState(authFlowState);

    useEffect(() => {
        listeners.add(setState);
        return () => listeners.delete(setState);
    }, []);

    return state;
};

const AuthFlowContext = createContext(null);

export const AuthFlowProvider = ({ children }) => {
    const state = useAuthFlowStore();
    const value = useMemo(() => ({
        ...state,
        updateRegistrationFlow,
        updateSSOFlow,
        clearRegistrationFlow,
        clearSSOFlow,
        resetAuthFlow,
    }), [state]);

    return <AuthFlowContext.Provider value={value}>{children}</AuthFlowContext.Provider>;
};

export const useAuthFlow = () => {
    const contextValue = useContext(AuthFlowContext);
    const state = useAuthFlowStore();

    return contextValue || {
        ...state,
        updateRegistrationFlow,
        updateSSOFlow,
        clearRegistrationFlow,
        clearSSOFlow,
        resetAuthFlow,
    };
};

export default AuthFlowContext;
